import React, { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

const CONFIG = {
  orders: { endpoint: "/orders", cols: [["number", "Nr."], ["customer_name", "Kunde"], ["total", "€"], ["status", "Status"]] },
  leads: { endpoint: "/leads", cols: [["name", "Name"], ["email", "E-Mail"], ["subject", "Betreff"], ["status", "Status"]] },
  payments: { endpoint: "/payments/transactions", cols: [["user_email", "E-Mail"], ["amount", "Betrag"], ["currency", "Währung"], ["payment_status", "Status"]] },
};

export default function Simple({ type }) {
  const { t, lang, sl } = useI18n();
  const de = lang === "de";
  const cfg = CONFIG[type];
  const { items, loading, reload: load } = useAdminData(cfg.endpoint);
  const [busy, setBusy] = useState(null);

  const canDelete = type === "orders" || type === "leads";
  const del = async (it) => {
    const isLead = type === "leads";
    const what = isLead ? `Lead „${it.name || it.email}"` : "Bestellung";
    if (!window.confirm(de
      ? `${what} wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
      : "Do you really want to permanently delete this record? This action cannot be undone.")) return;
    setBusy(it.id);
    try { await api.delete(`${isLead ? "/leads" : "/orders"}/${it.id}`); toast.success(de ? (isLead ? "Lead gelöscht" : "Bestellung gelöscht") : "Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-black">{t(`admin.${type}`)}</h1>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground"><tr>
            {cfg.cols.map(([, l]) => <th key={l} className="p-4">{l}</th>)}
            {canDelete && <th className="p-4 text-right">{t("common.actions")}</th>}
          </tr></thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={cfg.cols.length + (canDelete ? 1 : 0)} /> : items.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`${type}-row-${it.id}`}>
                {cfg.cols.map(([k]) => (
                  <td key={k} className="p-4">
                    {k === "status" || k === "payment_status"
                      ? <span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold capitalize">{sl(it[k])}</span>
                      : k === "total" || k === "amount" ? `${(it[k] || 0).toFixed ? it[k].toFixed(2) : it[k]} €`
                      : (it[k] || "—")}
                  </td>
                ))}
                {canDelete && (
                  <td className="p-4"><div className="flex justify-end">
                    <button onClick={() => del(it)} disabled={busy === it.id} className="btn-ghost !py-1.5 !px-3 text-xs text-red-500 hover:text-red-600" data-testid={`${type === "leads" ? "lead" : "order"}-delete-${it.id}`}>
                      {busy === it.id ? <Loader2 size={13} className="animate-spin" /> : <><Trash2 size={13} /> {de ? "Löschen" : "Delete"}</>}
                    </button>
                  </div></td>
                )}
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={cfg.cols.length + (canDelete ? 1 : 0)} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
